import axios from '../../common/utils/interceptor';
import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { apiUrls } from '../../constants';
import { FullPageLoader } from '../../common/components/FullPageLoader';
import { getCookie, setCookie } from '../../common/utils/cookies';
import { questionMap } from '../utils/questionMapper';
import { RoutePageProps } from '../../base/components/Routes';
import { TipsStyles } from '../styles/tips';
import { useParams } from 'react-router-dom';
import { surveyTypes } from '../constants/shl360';
import { PossibleQuestion, ResponseLabels, Response, Progress } from '../interfaces/surveyScreen';
import { Tips } from '../interfaces/Tips';
import { SurveyMeta } from '../interfaces/MFSPlayer';
import { LayoutStyles } from '../styles/common';
import useHiddenHeaderFooter from '../hooks/useHiddenHeaderFooter';

import ViewAllScreen from './layouts/ViewAllScreenR2';
import { flatQusData, getServerDomain } from '../utils/helper';
import { useAppSelector } from '../../store/hook';
const ThankYouScreen = React.lazy(() => import('./instructions/ThankYouScreen'));

let attemptedCount = 0;

/**
 * HOC Responsible
 * - Go to next question of survey
 * - End the survey workflow for last question and redirect back to task page
 */
const withSurveyFlow =
  <P extends RoutePageProps>(Component: React.ComponentType<P>): ((props: P) => React.JSX.Element) =>
  (props: P): React.JSX.Element => {
    const routeParams = useParams<{
      ratingId?: string;
      sId?: string;
      questId?: string;
      raterId?: string;
      langId?: string;
    }>();
    const { classes } = TipsStyles();
    const { classes: customClass } = LayoutStyles();

    const [loading, setLoading] = useState<boolean>(false);
    const [store, setStore] = useState<PossibleQuestion[]>([]);
    const [currentQus, setCurrentQus] = React.useState<number>(0);
    const [totalQus, setTotalQus] = React.useState<number>(0);
    const [questionData, setQuestionData] = React.useState<PossibleQuestion>({});
    const [showThankYou, setShowThankYou] = React.useState<boolean>(false);
    const [progress, setProgress] = React.useState<Progress>({ totalQus: 0, attempted: 0 });
    const [showViewAll, setShowViewAll] = React.useState<boolean>(false);
    const [QULabels, setQULabels] = React.useState<ResponseLabels[]>([]);
    const [IRLabels, setIRLabels] = React.useState<ResponseLabels[]>([]);
    const [tips, setTips] = React.useState<Tips>({ tipsTitle: '', tipsInstruction: '' });
    const [customThanks, setCustomThanks] = React.useState<string>('');
    const [surveyMeta, setSurveyMeta] = React.useState<SurveyMeta>({
      fociName: '',
      raterCategory: '',
      language: '',
    });
    const [isError, setIsError] = React.useState<boolean>(false);
    const [navigationType, setNavigationType] = React.useState('NEXT');
    const [isPreview] = React.useState<boolean>(!!(routeParams.sId && routeParams.questId));
    isPreview && useHiddenHeaderFooter(customClass.screenContainer);
    setCookie('isPreview', `${isPreview}`);
    /* const data = QuestionData.questionBody.importanceRatings[currentQus]; */
    /* eslint-disable react-hooks/exhaustive-deps */
    /* const type = data.type; */

    const getLabels = (type: string): ResponseLabels[] => {
      if (type === surveyTypes.QU) {
        return QULabels;
      } else if (type === surveyTypes.IR) {
        return IRLabels;
      } else {
        return [];
      }
    };

    const getQuestionToRender = (data: PossibleQuestion): PossibleQuestion => {
      const type = data.type;
      let question = data.itemId || data.itemID ? questionMap(data) : data;
      if (type === surveyTypes.QU || type === surveyTypes.IR) {
        question = { ...question, labels: getLabels(type) };
      }
      return question;
    };

    const getAttempedQus = (data: Array<PossibleQuestion>): void => {
      let attempted = 0;
      for (let i = 0; i < data.length; i++) {
        if (data[i].type !== surveyTypes.TIPS && data[i].type !== surveyTypes.INTRO) {
          if (data[i].selectedResponse !== null && data[i].selectedResponse !== '') {
            attempted++;
          }
        }
      }
      if (attempted !== totalQus) {
        setProgress({ totalQus, attempted });
        attemptedCount = attempted;
        setShowThankYou(false);
      } else {
        setShowThankYou(true);
      }
    };

    const backFromViewAll = (): void => {
      setShowViewAll(false);
    };

    const getUnAnsweredQus = (data: Array<PossibleQuestion>): void => {
      let index = 0;
      let attempted = 0;
      for (let i = 0; i < data.length; i++) {
        if (data[i].type !== surveyTypes.TIPS && data[i].type !== surveyTypes.INTRO) {
          if (data[i].selectedResponse !== null && data[i].selectedResponse !== '') {
            attempted++;
          } else {
            break;
          }
        }
        index++;
      }
      if (attempted !== totalQus) {
        setCurrentQus(attempted === 0 ? 0 : index);
        setProgress({ totalQus, attempted });
        attemptedCount = attempted;
      } else {
        setShowThankYou(true);
      }
    };

    const updateParentStore = (data: PossibleQuestion[]): void => {
      setStore(data);
      getUnAnsweredQus(store);
      getAttempedQus(data);
    };

    const fetchQuestionDetails = async (): Promise<void> => {
      try {
        const request = isPreview
          ? {
              questId: routeParams.questId && +routeParams.questId,
              ratingStatus: routeParams.raterId && +routeParams.raterId,
              langId: routeParams.langId && +routeParams.langId,
            }
          : { ratingID: routeParams.ratingId };
        const config = { headers: { sid: !isPreview ? getCookie('sid') : routeParams.sId }, withCredentials: false };
        // const { data } = await axios.get(isPreview ? apiUrls.GET_SURVEY_PREVIEW_QUESTIONS : apiUrls.GET_SURVEY_QUESTIONS);
        const { data } = await axios.post(
          getServerDomain(!isPreview ? apiUrls.GET_SURVEY_QUESTIONS : apiUrls.GET_SURVEY_PREVIEW_QUESTIONS),
          request,
          config,
        );
        setStore(flatQusData(data));
        setTips(data.tips);
        setQULabels(data.responseLabels.quResponseLabels);
        setIRLabels(data.responseLabels.irResponseLabels);
        setTotalQus(data.totalQuestions);
        setSurveyMeta({
          fociName: data.fociName,
          raterCategory: data.raterCategory,
          language: data.selectedLanguage || 'EN_GB',
        });
      } catch (e) {
        setIsError(true);
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    const fetchCustomThankYou = async (): Promise<void> => {
      try {
        const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
        const { data } = await axios.get(getServerDomain(apiUrls.GET_CUSTOM_THANKYOU_MESSAGE), config);
        setCustomThanks(data.content);
      } catch (e) {
        console.log(e);
      }
    };

    const moveToNextQuestion = (): void => {
      setNavigationType('NEXT');
      setIsError(false);
      if (questionData.itemId || questionData.itemID) {
        attemptedCount = attemptedCount + 1;
      }
      setProgress({ totalQus, attempted: attemptedCount });
      if (attemptedCount < totalQus) {
        setCurrentQus(currentQus + 1);
      } else {
        setShowThankYou(true);
      }
    };

    const moveWithoutProgress = (): void => {
      setNavigationType('NEXT');
      setCurrentQus(currentQus + 1);
    };

    const moveToPrevQuestion = (): void => {
      setNavigationType('BACK');
      if (currentQus > 0) {
        setCurrentQus(currentQus - 1);
      }
    };

    const submitResponse = async (res: Response, seq?: number | string): Promise<void> => {
      const tempStore = store;
      try {
        const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
        const { data } = await axios.post(getServerDomain(apiUrls.POST_SURVEY_ANSWERS), res, config);
        // const data = { status: 'SUCCESS' };
        if (data.status === 'SUCCESS') {
          tempStore[seq as number].selectedResponse = res.response;
          setStore(tempStore);
          setIsError(false);
        } else {
          setIsError(true);
          throw new Error('error');
        }
      } catch (e) {
        setIsError(true);
        document?.querySelector('#answers button.btnSelected')?.classList.remove('btnSelected');
        throw new Error('error');
      }
    };

    const updateSelectedResponse = async (data: string): Promise<string> => {
      let Status = '';
      const tempStore = store;
      if (questionData.itemId || questionData.itemID) {
        const req = {
          type: questionData.type,
          itemId: questionData.itemId || questionData.itemID,
          scaleId: questionData.type === surveyTypes.QU ? questionData.scaleId : null,
          response: data,
        };
        try {
          !isPreview ? await submitResponse(req, currentQus) : (tempStore[currentQus].selectedResponse = req.response);
          isPreview && setStore(tempStore);
          Status = 'SUCCESS';
          setIsError(false);
        } catch (ex) {
          Status = 'ERROR';
          setIsError(true);
        }
      }
      return Status;
    };

    const { exit } = useAppSelector((state) => state.headerOptions);
    useEffect(() => {
      if (!store.length && !loading) {
        setLoading(true);
        setIsError(false);
        fetchQuestionDetails();
        fetchCustomThankYou();
      }
    }, []);

    useEffect(() => {
      totalQus !== 0 && getUnAnsweredQus(store);
      totalQus !== 0 && getAttempedQus(store);
    }, [totalQus]);

    useEffect(() => {
      store.length && store[currentQus] && setQuestionData(getQuestionToRender(store[currentQus]));
    }, [store, currentQus]);

    return (
      <>
        <FullPageLoader isLoading={loading} customClass={customClass.screenContainer} />
        {showThankYou && (
          <div className={classes.screenManager}>
            <div className="thankYouScreen">
              <ThankYouScreen setShowViewAll={setShowViewAll} customMessage={customThanks} isPreview={isPreview} />
            </div>
          </div>
        )}

        {showViewAll && (
          <div className={classes.screenManager}>
            <ViewAllScreen
              questionData={store.slice()}
              QULabels={QULabels}
              IRlabels={IRLabels}
              questionProgress={{ totalQus: totalQus, attempted: attemptedCount }}
              setProgress={setProgress}
              showThankYou={setShowThankYou}
              backFromViewAll={backFromViewAll}
              updateParentStore={updateParentStore}
              isPreview={isPreview}
            />
          </div>
        )}

        <Box className={!showViewAll && !exit && !showThankYou ? classes.show : classes.hide}>
          <Component
            {...props}
            tips={tips}
            surveyMeta={surveyMeta}
            questionData={questionData}
            moveToNextQuestion={moveToNextQuestion}
            moveToPrevQuestion={moveToPrevQuestion}
            moveWithoutProgress={moveWithoutProgress}
            currentQusIndex={currentQus}
            updateSelectedResponse={updateSelectedResponse}
            setShowViewAll={setShowViewAll}
            progress={progress}
            isError={isError}
            navigationType={navigationType}
            isPreview={isPreview}
          />
        </Box>
      </>
    );
  };

export default withSurveyFlow;
